// ----------------------------------------------------------------------
// <copyright file="Izenda.CalculatedFieldPropertiesSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { get, find, placeholder } from 'lodash/fp';

import { BUNDLE } from 'logic/data/Izenda.ReportDesignerConstants';
import localizer from 'share/localization/Izenda.Localizer';
const { MESSAGE: MESSAGES } = BUNDLE;

export default function(editorComp, reportDesignerModel, fieldStore, itemProps) {
  const configField = itemProps.dataObject;
  const reportFields = reportDesignerModel.reportFields;
  const SCHEMA_DEF = {
    title: localizer.getText('CALCULATED_FIELD_PROPERTIES_SCHEMA_LABEL_FIELD_PROPERTIES'),
    schemaKey: 'DATASOURCE_CALCULATED_FIELD_PROPERTIES',
    props: {
      className: 'form-horizontal field-props-container'
    },
    fields: {
      wrapperContent: {
        component: 'ScrollBoxContainer',
        props: {
          className: 'scrollbox-container'
        },
        fields: {
          dataSource: {
            component: 'Panel',
            props: {
              initState: true,
              collapsible: true,
              header: {
                title: localizer.getText('COMMON_LABEL_DATA_SOURCE'),
                icon: 'icon-toggle-triangle'
              }
            },

            fields: {
              // Type
              fieldType: {
                title: localizer.getText('COMMON_LABEL_TYPE'),
                type: 'static',
                fnValue: 'getDataObjectType'
              },
              // Field Name
              name: {
                title: localizer.getText('COMMON_LABEL_FIELD_NAME'),
                type: 'text',
                props: {
                  onBlur: 'onBlurFieldName',
                  validations: {
                    presence: {
                      presence: true,
                      message: localizer.getText('CALCULATED_FIELD_PROPERTIES_SCHEMA_MESSAGE_FIELD_NAME')
                    }
                  },
                  field: 'fieldName'
                }
              },
              // Data Type
              izendaDataType: {
                title: localizer.getText('COMMON_LABEL_DATA_TYPE'),
                type: 'static'
              },
              // Expression
              expression: {
                title: localizer.getText('COMMON_LABEL_EXPRESSION'),
                component: 'ExpressionInput',
                fnValue: 'getExpression',
                props: {
                  title: localizer.getText('CALCULATED_FIELD_CONSTANTS_FUNCTION_TYPE_EDIT_TITLE'),
                  loadValue: 'getExpression'
                },
                staticProps: {
                  className: 'icon-setting btn-icon expression-icon',
                  reportDesignerModel: reportDesignerModel,
                  fieldStore: itemProps.store
                }
              }
            }
          }
        }
      }
    }
  };

  return {
    // define data to render UI
    definedSchema: SCHEMA_DEF,

    // list of widgets which are used to render UI
    widgets: {
      Panel: require('share/components/controls/Izenda.Panel'),
      ExpressionInput: require('share/components/controls/Izenda.ExpressionInput'),
      ScrollBoxContainer: require('share/components/controls/Izenda.ScrollBoxContainer')
    },

    // data to binding to fields
    source: (itemProps && itemProps.itemData) || {},

    // properties for different fields
    mappingProps: {
      onBlurFieldName: e => {
        e.preventDefault();
        e.stopPropagation();
        const validation = itemProps.validation;
        const localization = itemProps.localization;
        const newValue = e.target.value.trim();

        // check require
        if (!newValue) {
          validation.updateErrors([
            {
              key: 'fieldName',
              messages: [MESSAGES.M01]
            }
          ]);
          return;
        }

        // check contain character ']'
        if (newValue && (newValue.indexOf(']') > -1 || newValue.indexOf('[') > -1)) {
          validation.updateErrors([
            {
              key: 'fieldName',
              messages: localization.getText('CALCULATED_FIELD_MESSAGE_INVALID_NAME')
            }
          ]);
          return;
        }

        // check dupplicate
        const matchingField = placeholder(reportFields.dataTree.details)
          .chain()
          .flatMap(item => item.querySource.toJSON())
          .flatMap(querySource => querySource.fields.toJSON())
          .find(element => element.isCalculated && element.name === newValue && element.id !== itemProps.itemData.id)
          .value();

        if (matchingField) {
          validation.updateErrors([
            {
              key: 'fieldName',
              messages: [MESSAGES.M08]
            }
          ]);
        } else {
          if (itemProps.itemData.name !== newValue) {
            itemProps.itemData.name = newValue;
            if (configField.updateData(itemProps.itemData)) {
              itemProps.store.addCalculatedField(itemProps.itemData);
            }
          }
        }
      },

      getExpression: () => {
        return itemProps && itemProps.itemData;
      }
    },

    // defined-function which fields need to execute to get its data
    mappingSource: {
      getDataObjectName: () => {
        return itemProps.itemData && itemProps.itemData.name;
      },

      getDataObjectType: () => {
        return 'Calculated Field';
      },

      getExpression: () => {
        return itemProps && itemProps.itemData;
      }
    },

    onChangeValue: (schemaData, changedKey, changedKeyPath, changedValue, changedOthersInfo) => {
      const groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
      const fieldData = groupData[changedKey];

      fieldData && (fieldData.value = changedValue);

      // Just update for calculated field data, not for separate property
      const data = schemaData.wrapperContent.dataSource.expression.value;
      if (configField.updateData(data) && changedKey === 'expression') {
        // Reset data for lazyloading
        const filterDetailsConfig = get('reportFilter.configField', reportFields);
        filterDetailsConfig && filterDetailsConfig.resetOperatorSourceDisplay();

        // Reset the values of filter user set
        const filterFields = get('reportFilter.filterFields', reportFields);
        const filterDetails = find({ sourceFieldName: data.name }, filterFields);
        filterDetails && filterDetails.updateByKey('value', null);

        itemProps.store.addCalculatedField(data).then(() => {
          // update name value
          schemaData.wrapperContent.dataSource.name.value = data.name;
          itemProps.validation.reset();
        });
      }
    }
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSourceTree/Izenda.CalculatedFieldPropertiesSchema.js